import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import GradientButton from '../../components/buttons/GradientButton';
import ActionButton from '../../shared/components/action-button/ActionButton';
import { buildFullAddress, normalizeLocationAddress } from '../locationTypes';
import {
  buildDefaultBusinessHoursState,
  formatBusinessHoursForStorage,
  parseStoredBusinessHours,
  resolveLocationOpenOnHolidays,
} from '../business-hours/locationBusinessHoursUtils';
import LocationBusinessHoursEditor from './LocationBusinessHoursEditor';
import './LocationsSettings.css';

const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
];

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Phoenix', label: 'Mountain Time - Arizona (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
];

const emptyForm = (companyData) => ({
  name: '',
  phone: companyData?.phone || '',
  timezone: companyData?.businessInfo?.timezone || 'America/New_York',
  active: true,
  isDefault: false,
  useCompanyHours: true,
  address: {
    street1: '',
    street2: '',
    city: '',
    state: '',
    zipCode: '',
  },
});

const LocationEditorForm = ({
  location,
  companyData,
  saving = false,
  onCancel,
  onSave,
}) => {
  const [form, setForm] = useState(emptyForm(companyData));
  const [customHours, setCustomHours] = useState(() => buildDefaultBusinessHoursState());
  const [openOnHolidays, setOpenOnHolidays] = useState(
    () => companyData?.settings?.openOnHolidays ?? false
  );

  const isDefaultLocation = location?.isDefault === true;

  useEffect(() => {
    if (location) {
      const usesCompanyHours = isDefaultLocation || !location.businessHours;
      setForm({
        name: location.name || '',
        phone: location.phone || '',
        timezone: location.timezone || companyData?.businessInfo?.timezone || 'America/New_York',
        active: location.active !== false,
        isDefault: location.isDefault === true,
        useCompanyHours: usesCompanyHours,
        address: normalizeLocationAddress(location.address),
      });
      setCustomHours(
        usesCompanyHours
          ? parseStoredBusinessHours(companyData?.settings?.companyHours)
          : parseStoredBusinessHours(location.businessHours)
      );
      setOpenOnHolidays(
        usesCompanyHours
          ? companyData?.settings?.openOnHolidays ?? false
          : resolveLocationOpenOnHolidays(location, companyData)
      );
      return;
    }

    setForm(emptyForm(companyData));
    setCustomHours(parseStoredBusinessHours(companyData?.settings?.companyHours));
    setOpenOnHolidays(companyData?.settings?.openOnHolidays ?? false);
  }, [location, companyData, isDefaultLocation]);

  const canSave = useMemo(() => String(form.name || '').trim().length > 0, [form.name]);
  const usesCompanyHours = isDefaultLocation || form.useCompanyHours;

  const updateAddress = (field, value) => {
    setForm((prev) => {
      const nextAddress = { ...prev.address, [field]: value };
      return {
        ...prev,
        address: {
          ...nextAddress,
          fullAddress: buildFullAddress(nextAddress),
        },
      };
    });
  };

  const handleUseCompanyHoursChange = (checked) => {
    setForm((prev) => ({ ...prev, useCompanyHours: checked }));
    if (checked) {
      setCustomHours(parseStoredBusinessHours(companyData?.settings?.companyHours));
      setOpenOnHolidays(companyData?.settings?.openOnHolidays ?? false);
      return;
    }
    setCustomHours(
      parseStoredBusinessHours(location?.businessHours || companyData?.settings?.companyHours)
    );
    setOpenOnHolidays(
      location?.businessHours
        ? resolveLocationOpenOnHolidays(location, companyData)
        : companyData?.settings?.openOnHolidays ?? false
    );
  };

  const handleDefaultToggle = (checked) => {
    setForm((prev) => ({
      ...prev,
      isDefault: checked,
      useCompanyHours: checked ? true : prev.useCompanyHours,
    }));
    if (checked) {
      setCustomHours(parseStoredBusinessHours(companyData?.settings?.companyHours));
      setOpenOnHolidays(companyData?.settings?.openOnHolidays ?? false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!canSave || saving) return;

    const shouldUseCompanyHours = isDefaultLocation || form.isDefault || form.useCompanyHours;

    onSave?.({
      name: form.name.trim(),
      phone: form.phone.trim(),
      timezone: form.timezone,
      active: form.active,
      isDefault: form.isDefault,
      useCompanyHours: shouldUseCompanyHours,
      address: normalizeLocationAddress(form.address),
      businessHours: shouldUseCompanyHours
        ? null
        : formatBusinessHoursForStorage(customHours),
      openOnHolidays: shouldUseCompanyHours ? null : openOnHolidays,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="locations-settings__editor-form">
      <div className="biFormGrid">
        <div className="biField" style={{ gridColumn: '1 / -1' }}>
          <div className="biFieldLabelRow">
            <label className="biLabel" htmlFor="loc-name">Location name</label>
          </div>
          <input
            id="loc-name"
            className="biInput"
            value={form.name}
            onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
            placeholder="Downtown, West Side, etc."
            disabled={saving}
            required
          />
        </div>

        <div className="biField">
          <div className="biFieldLabelRow">
            <label className="biLabel" htmlFor="loc-phone">Phone</label>
          </div>
          <input
            id="loc-phone"
            className="biInput"
            value={form.phone}
            onChange={(event) => setForm((prev) => ({ ...prev, phone: event.target.value }))}
            disabled={saving}
          />
        </div>

        <div className="biField">
          <div className="biFieldLabelRow">
            <label className="biLabel" htmlFor="loc-timezone">Timezone</label>
          </div>
          <select
            id="loc-timezone"
            className="biInput"
            value={form.timezone}
            onChange={(event) => setForm((prev) => ({ ...prev, timezone: event.target.value }))}
            disabled={saving}
          >
            {TIMEZONES.map((timezone) => (
              <option key={timezone.value} value={timezone.value}>
                {timezone.label}
              </option>
            ))}
          </select>
        </div>

        <div className="biField" style={{ gridColumn: '1 / -1' }}>
          <div className="biFieldLabelRow">
            <label className="biLabel" htmlFor="loc-street1">Street address</label>
          </div>
          <input
            id="loc-street1"
            className="biInput"
            value={form.address.street1}
            onChange={(event) => updateAddress('street1', event.target.value)}
            disabled={saving}
          />
        </div>

        <div className="biField" style={{ gridColumn: '1 / -1' }}>
          <div className="biFieldLabelRow">
            <label className="biLabel" htmlFor="loc-street2">Suite / Unit #</label>
            <span className="biHint">Optional</span>
          </div>
          <input
            id="loc-street2"
            className="biInput"
            value={form.address.street2}
            onChange={(event) => updateAddress('street2', event.target.value)}
            disabled={saving}
          />
        </div>

        <div className="biField">
          <div className="biFieldLabelRow">
            <label className="biLabel" htmlFor="loc-city">City</label>
          </div>
          <input
            id="loc-city"
            className="biInput"
            value={form.address.city}
            onChange={(event) => updateAddress('city', event.target.value)}
            disabled={saving}
          />
        </div>

        <div className="biField">
          <div className="biFieldLabelRow">
            <label className="biLabel" htmlFor="loc-state">State</label>
          </div>
          <select
            id="loc-state"
            className="biInput"
            value={form.address.state}
            onChange={(event) => updateAddress('state', event.target.value)}
            disabled={saving}
          >
            <option value="">Select…</option>
            {US_STATES.map((state) => (
              <option key={state.value} value={state.value}>{state.label}</option>
            ))}
          </select>
        </div>

        <div className="biField">
          <div className="biFieldLabelRow">
            <label className="biLabel" htmlFor="loc-zip">ZIP code</label>
          </div>
          <input
            id="loc-zip"
            className="biInput"
            value={form.address.zipCode}
            onChange={(event) => updateAddress('zipCode', event.target.value)}
            disabled={saving}
          />
        </div>
      </div>

      <div className="biDivider" />

      {isDefaultLocation || form.isDefault ? (
        <div className="locations-settings__hours-default-note">
          <h2 className="locations-settings__section-title">Business hours</h2>
          <p className="biHelperText">
            The default location always follows your company business hours. Update them in{' '}
            <Link to="/app/overview/settings?business-hours">
              Settings → Business hours
            </Link>
            .
          </p>
        </div>
      ) : (
        <>
          <label className={`bi-check-row${saving ? ' bi-check-row--disabled' : ''}`}>
            <input
              type="checkbox"
              className="bi-checkbox"
              checked={usesCompanyHours}
              onChange={(event) => handleUseCompanyHoursChange(event.target.checked)}
              disabled={saving}
            />
            <span className="bi-check-row__body">
              <span className="bi-check-row__title">Use company default business hours</span>
              <span className="bi-check-row__desc">
                This location follows your main company schedule from Business hours settings.
              </span>
            </span>
          </label>

          {!usesCompanyHours ? (
            <LocationBusinessHoursEditor
              hours={customHours}
              onChange={setCustomHours}
              openOnHolidays={openOnHolidays}
              onOpenOnHolidaysChange={setOpenOnHolidays}
              disabled={saving}
            />
          ) : null}
        </>
      )}

      <div className="biDivider" />

      <label className={`bi-check-row${saving ? ' bi-check-row--disabled' : ''}`}>
        <input
          type="checkbox"
          className="bi-checkbox"
          checked={form.isDefault}
          onChange={(event) => handleDefaultToggle(event.target.checked)}
          disabled={saving || isDefaultLocation}
        />
        <span className="bi-check-row__body">
          <span className="bi-check-row__title">Set as default location</span>
          <span className="bi-check-row__desc">
            Used when a booking does not specify a location. Default locations always use company hours.
          </span>
        </span>
      </label>

      <div className="biDivider" />

      <label className={`bi-check-row${saving ? ' bi-check-row--disabled' : ''}`}>
        <input
          type="checkbox"
          className="bi-checkbox"
          checked={form.active}
          onChange={(event) => setForm((prev) => ({ ...prev, active: event.target.checked }))}
          disabled={saving}
        />
        <span className="bi-check-row__body">
          <span className="bi-check-row__title">Active</span>
          <span className="bi-check-row__desc">Visible for booking and staff assignment.</span>
        </span>
      </label>

      <div className="biActions">
        <ActionButton type="button" onClick={onCancel} disabled={saving}>
          Cancel
        </ActionButton>
        <GradientButton type="submit" variant="primary" disabled={!canSave || saving}>
          {saving ? 'Saving…' : 'Save location'}
        </GradientButton>
      </div>
    </form>
  );
};

export default LocationEditorForm;
